import {proxy} from "valtio";

import type {CollapsibleRow, ExpectedChargeRow, Quote} from "@/entities/booking";

import {IValtioModel} from "@/shared/models/base-model.interface";

interface PricingSummaryConfig {
    quote: Quote;
    nights: number;
}

class PricingSummaryClass implements IValtioModel {
    private quote: Quote;
    private readonly nights: number;

    constructor(config: PricingSummaryConfig) {
        this.quote = config.quote;
        this.nights = config.nights;
    }

    /** Replace quote with updated data from API (e.g. after updateCheckout) */
    updateQuote(quote: Quote): void {
        this.quote = quote;
    }

    // ==================== Currency ====================

    get currency(): string {
        return this.quote.currency;
    }

    get nightsCount(): number {
        return this.nights;
    }

    get rentAmount(): string {
        return this.formatCurrency(this.quote.rent.total);
    }

    get feesRows(): Array<CollapsibleRow> {
        return this.quote.fees.breakdown.map((fee) => ({
            label: fee.label,
            amount: this.formatCurrency(fee.amount),
        }));
    }

    get taxesRows(): Array<CollapsibleRow> {
        return this.quote.taxes.breakdown.map((tax) => ({
            label: tax.label,
            amount: this.formatCurrency(tax.amount),
        }));
    }

    /** Active optional fees for display in summary (active=true) */
    get optionalFeesRows(): Array<CollapsibleRow> {
        return (
            this.quote.optionalFees?.breakdown
                ?.filter((fee) => fee.active)
                ?.map((fee) => ({label: fee.label, amount: this.formatCurrency(fee.amount)})) ?? []
        );
    }

    /** Formatted coupon/promo discount as negative amount (e.g. "-$50.00") for display */
    get couponDiscountAmount(): string | null {
        const discount = this.quote.couponDiscount;
        if (discount == null || discount <= 0) return null;
        return this.formatCurrency(-discount);
    }

    get securityDepositsRows(): Array<CollapsibleRow> {
        return (
            this.quote.securityDeposits?.breakdown?.map((item) => ({
                label: item.label,
                amount: this.formatCurrency(item.amount),
            })) ?? []
        );
    }

    get totalWithDeposits(): string | null {
        const deposit = this.quote.securityDeposits?.total;
        if (!deposit || deposit === 0) return null;
        return this.formatCurrency(this.quote.total + deposit);
    }

    get expectedChargesRows(): Array<ExpectedChargeRow> {
        const charges = this.quote.expectedCharges;
        if (!charges || charges.length === 0) return [];

        const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;
        const localToday = new Date().toLocaleDateString("en-CA", {
            timeZone,
        });

        const grouped = new Map<string, number>();
        for (const charge of charges) {
            const date = charge.date.slice(0, 10);
            grouped.set(date, (grouped.get(date) ?? 0) + charge.amount);
        }

        return Array.from(grouped.entries()).map(([date, amount]) => ({
            labelKey: date === localToday ? "dueToday" : "dueOn",
            date: date === localToday ? undefined : this.formatChargeDate(date),
            amount: this.formatCurrency(amount),
        }));
    }

    private formatChargeDate(isoDate: string): string {
        return new Date(isoDate).toLocaleDateString("en-US", {timeZone: "UTC", month: "short", day: "numeric"});
    }

    get totalAmount(): string {
        return this.formatCurrency(this.quote.total);
    }

    get totalRaw(): number {
        return this.quote.total;
    }

    /** Total WITHOUT optional fees — used in the "decline protection" label */
    get baseTotalAmount(): string {
        const optionalFeesTotal = this.quote.optionalFees?.breakdown?.filter((fee) => fee.active).reduce((sum, fee) => sum + fee.amount, 0) ?? 0;
        return this.formatCurrency(this.quote.total - optionalFeesTotal);
    }

    private formatCurrency(amount: number): string {
        return new Intl.NumberFormat("en-US", {
            style: "currency",
            currency: this.currency,
        }).format(amount);
    }

    init(): void | Promise<void> {
        // No initialization needed
    }
}

export type PricingSummaryInstance = PricingSummaryClass;

export function PricingSummary(config: PricingSummaryConfig): PricingSummaryInstance {
    return proxy(new PricingSummaryClass(config));
}
